import { useCallback, useEffect, useRef, useState } from "react";

import {
  NIVEAUX,
  api,
  type Document,
  type JeuMessages,
  type Messages,
  type ResultatsQcm,
} from "./api";
import { ApercuTypst } from "./Maths";
import { couleursNiveaux, libellesNiveaux, type NiveauMaitrise } from "./palette";

const LIBELLES_COURTS: Record<string, string> = {
  non_acquis: "Non acquis",
  en_cours: "En cours",
  fragile: "Fragile",
  acquis: "Acquis",
};

const DELAI_ENREGISTREMENT = 900;

export default function BilansQcm({ qcmId }: { qcmId: number }) {
  const [resultats, setResultats] = useState<ResultatsQcm | null>(null);
  const [appreciations, setAppreciations] = useState<Record<number, string>>({});
  const [documents, setDocuments] = useState<Document[]>([]);
  const [messages, setMessages] = useState<Messages>({});
  const [etat, setEtat] = useState<"repos" | "redaction" | "production">("repos");
  const [message, setMessage] = useState<string | null>(null);
  // Le bandeau du haut de section est hors de vue quand on enregistre les
  // messages — le bouton est douze zones de texte plus bas. Sans accusé de
  // réception à côté de lui, l'enregistrement réussit sans que rien ne
  // change à l'écran : le bouton paraît inerte.
  const [confirmation, setConfirmation] = useState<string | null>(null);

  const enAttente = useRef<Map<number, string>>(new Map());
  const minuteur = useRef<number | undefined>(undefined);

  const recharger = useCallback(() => {
    api.lireResultatsQcm(qcmId).then(setResultats);
    api.lireAppreciationsQcm(qcmId).then(setAppreciations);
    api.lireDocumentsQcm(qcmId).then(setDocuments);
    api.lireMessagesQcm(qcmId).then(setMessages);
  }, [qcmId]);

  useEffect(recharger, [recharger]);

  // Écriture différée, comme la saisie des réponses : le professeur écrit au
  // fil de l'eau, on ne l'interrompt pas d'un aller-retour par frappe.
  const ecrire = useCallback(async () => {
    const lots = [...enAttente.current.entries()];
    enAttente.current.clear();
    await Promise.all(
      lots.map(([copieId, texte]) => api.enregistrerAppreciationQcm(copieId, texte)),
    );
  }, []);

  const modifier = (copieId: number, texte: string) => {
    setAppreciations((precedent) => ({ ...precedent, [copieId]: texte }));
    enAttente.current.set(copieId, texte);
    window.clearTimeout(minuteur.current);
    minuteur.current = window.setTimeout(ecrire, DELAI_ENREGISTREMENT);
  };

  const rediger = async () => {
    setEtat("redaction");
    setMessage(null);
    try {
      const proposition = await api.redigerMessagesQcm(qcmId);
      await api.enregistrerMessagesQcm(qcmId, proposition.jeu);
      setMessages(await api.lireMessagesQcm(qcmId));
      const bloquantes = proposition.anomalies.filter(
        (a) => a.gravite === "erreur",
      );
      setMessage(
        (bloquantes.length > 0
          ? `Enregistré avec ${bloquantes.length} anomalie(s) : ${bloquantes[0].message}`
          : "Messages rédigés et enregistrés.") +
          ` (${proposition.cout_dollars?.toFixed(3)} $, ` +
          (proposition.corrigees > 0
            ? `d'après ${proposition.corrigees} copies saisies)`
            : "avant correction)"),
      );
    } catch (e) {
      setMessage((e as Error).message);
    } finally {
      setEtat("repos");
    }
  };

  const modifierMessage = (code: string, niveau: string, texte: string) => {
    setConfirmation(null);   // retouché, donc plus enregistré
    setMessages((precedent) => ({
      ...precedent,
      [code]: { ...precedent[code], [niveau]: texte },
    }));
  };

  const enregistrerMessages = async () => {
    if (!resultats) return;
    const jeu: JeuMessages = {
      messages: resultats.competences
        .filter((c) => messages[c.code])
        .map((c) => ({
          code: c.code,
          non_acquis: messages[c.code].non_acquis ?? "",
          en_cours: messages[c.code].en_cours ?? "",
          fragile: messages[c.code].fragile ?? "",
          acquis: messages[c.code].acquis ?? "",
        })),
    };
    try {
      const bilan = await api.enregistrerMessagesQcm(qcmId, jeu);
      setConfirmation(`${bilan.enregistres} messages enregistrés.`);
    } catch (e) {
      setConfirmation((e as Error).message);
    }
  };

  const produire = async () => {
    await ecrire();
    setEtat("production");
    setMessage(null);
    try {
      const document = await api.produireBilansQcm(qcmId);
      setMessage(`${Math.round(document.octets / 1024)} ko produits.`);
      setDocuments(await api.lireDocumentsQcm(qcmId));
    } catch (e) {
      setMessage((e as Error).message);
    } finally {
      setEtat("repos");
    }
  };

  if (!resultats) return <p>Chargement…</p>;

  const presentes = resultats.copies.filter((c) => c.statut === "presente");
  const dernier = documents.find((d) => d.type === "feedback_individuel");
  const complet =
    resultats.competences.length > 0 &&
    resultats.competences.every((c) => messages[c.code]);

  return (
    <section className="bilans">
      <header className="saisie-entete">
        <div>
          <h2>Bilans individuels</h2>
          <p className="sous-titre">
            {presentes.length} copies présentes · {resultats.competences.length}{" "}
            compétences
          </p>
        </div>
        <div className="actions">
          <button
            type="button"
            onClick={rediger}
            disabled={etat !== "repos" || resultats.competences.length === 0}
            title={
              resultats.competences.length === 0
                ? "Identifier les compétences d'abord"
                : undefined
            }
          >
            {etat === "redaction"
              ? "Rédaction en cours…"
              : complet
                ? "Réécrire les messages (payant)"
                : "Rédiger les messages (payant)"}
          </button>
          <button
            type="button"
            onClick={produire}
            disabled={etat !== "repos" || presentes.length === 0}
          >
            {etat === "production" ? "Composition…" : "Produire les bilans"}
          </button>
          {dernier && (
            <a className="telechargement" href={api.lienDocumentQcm(dernier.id)}>
              Télécharger le PDF
            </a>
          )}
        </div>
      </header>

      {message && <p className="message">{message}</p>}

      <p className="aide">
        Une page par élève : son score, sa place dans la distribution de la
        classe, et le relevé de ses réponses — chaque erreur nommée par le
        diagnostic que l'auteur a écrit à côté du distracteur. L'annotation
        qualifie la réponse, jamais l'élève.
      </p>

      {resultats.competences.length === 0 && (
        <p className="aide">
          Aucune compétence n'est rattachée aux questions : le tableau des
          compétences ne s'imprimera pas, et les messages de remédiation, qui
          s'appuient sur elles, restent hors de portée. Passez d'abord par
          l'onglet <strong>Compétences</strong>.
        </p>
      )}

      {resultats.competences.length > 0 && (
        <>
          <h3>Messages de remédiation</h3>
          <p className="aide">
            Écrits une fois pour le QCM, ils s'affichent dans « Vos axes de
            travail » sur la fiche de chaque élève, selon le niveau qu'il a
            atteint. La rédaction s'appuie sur les erreurs que vous avez
            nommées à côté de chaque distracteur — et, dès que la saisie
            avance, sur celles qui ont été effectivement choisies. Ces
            effectifs orientent la rédaction ; ils n'apparaissent jamais dans
            le texte, le bilan étant individuel.
          </p>

          {resultats.competences.map((competence) => (
            <article key={competence.code} className="fiche-competence">
              <header>
                <strong>{competence.code}</strong>
                <span>{competence.libelle}</span>
              </header>
              {NIVEAUX.map((niveau) => (
                <div key={niveau} className="ligne-message">
                  <span
                    className="pastille-niveau"
                    style={{ background: couleursNiveaux[niveau as NiveauMaitrise] }}
                    title={libellesNiveaux[niveau as NiveauMaitrise]}
                  />
                  <label>{LIBELLES_COURTS[niveau]}</label>
                  <div className="champ-message">
                    <textarea
                      rows={2}
                      value={messages[competence.code]?.[niveau] ?? ""}
                      placeholder="Non rédigé"
                      onChange={(e) =>
                        modifierMessage(competence.code, niveau, e.target.value)
                      }
                    />
                    <ApercuTypst texte={messages[competence.code]?.[niveau] ?? ""} />
                  </div>
                </div>
              ))}
            </article>
          ))}

          <div className="ligne-bouton">
            <button type="button" onClick={enregistrerMessages}>
              Enregistrer les messages
            </button>
            {confirmation && <span className="message">{confirmation}</span>}
          </div>
        </>
      )}

      <h3>Appréciations</h3>
      <p className="aide">
        Facultatives, et de votre main : elles n'ont pas d'équivalent
        automatique. Enregistrées au fil de la frappe.
      </p>
      <table className="appreciations">
        <tbody>
          {presentes.map((copie) => (
            <tr key={copie.copie_id}>
              <th>
                {copie.nom} {copie.prenom}
                <div className="note-eleve">
                  {copie.note_finale.toLocaleString("fr-FR")} /{" "}
                  {resultats.total_points.toLocaleString("fr-FR")}
                </div>
                <div className="pastilles">
                  {copie.competences.map((competence) => (
                    <span
                      key={competence.code}
                      className="pastille-niveau"
                      style={{
                        background:
                          couleursNiveaux[competence.niveau as NiveauMaitrise],
                      }}
                      title={`${competence.libelle} — ${
                        libellesNiveaux[competence.niveau as NiveauMaitrise]
                      }`}
                    />
                  ))}
                </div>
              </th>
              <td>
                <textarea
                  rows={2}
                  value={appreciations[copie.copie_id] ?? ""}
                  placeholder="Appréciation (facultative)"
                  onChange={(e) => modifier(copie.copie_id, e.target.value)}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
